import { Skeleton, Stack } from "@mui/material";
import { BodyTextS, cs, HeadingM, useIsDesktop } from "@repo/ui";

import { type GetLifeDetailV2Response, ProductType } from "@repo/api";
import { type FC, type PropsWithChildren, useMemo } from "react";
import { CarretWrapper } from ".";
import { contractLink, NumberToCZK } from "../../../functions";
import { useLifeDetail } from "../../../queryHooks";
import { useFormatFufiTitle } from "../../ZivotniPojisteni/FufiPage/utils";
import { findFufiFirstPerson } from "../../ZivotniPojisteni/Heading/Subtitle";
import { DetailLink } from "../ContractCardDesktopMenu";
import {
    CaretLeftIcon,
    CaretRightIcon,
    HeaderAmount,
    HeaderContainer,
    HeaderPersonDescription,
    HeaderSubtitle,
    HeaderTitle,
    MobileCarretsRow,
    MobileLinkWrapper,
} from "./ContractCardHeader.styled";

export type ContractHeaderProps = {
    title?: string;
    subtitle?: string;
    personDescription?: string | null;
    link?: string;
    amount?: string;
};

export const ContractCardHeaderView = ({ amount, link, personDescription, subtitle, title }: ContractHeaderProps) => {
    const isDesktop = useIsDesktop();

    const personDescriptionElement = (() => {
        if (personDescription === null) return null;
        return <BodyTextS component={HeaderPersonDescription}>{personDescription ?? <Skeleton />}</BodyTextS>;
    })();

    return (
        <HeaderContainer data-test="contract">
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={4}>
                <HeadingM component={HeaderTitle}>{title ?? <Skeleton />}</HeadingM>
            </Stack>
            <BodyTextS component={HeaderSubtitle} light>
                {subtitle ? subtitle : <Skeleton />}
            </BodyTextS>
            {personDescriptionElement}
            {amount && <BodyTextS component={HeaderAmount}>{amount}</BodyTextS>}
            {!amount && link && <Skeleton />}
            {!isDesktop && link && (
                <MobileLinkWrapper>
                    <DetailLink link={link} />
                </MobileLinkWrapper>
            )}
        </HeaderContainer>
    );
};

type IContractHeaderWithPensionData = {
    contractId: string;
    proposalNumber?: string | null;
};

export const ContractHeaderWithFufiData: FC<IContractHeaderWithPensionData> = ({ contractId, proposalNumber }) => {
    const formatFufiTitle = useFormatFufiTitle();
    const isDesktop = useIsDesktop();
    const { data, isSuccess, isError } = useLifeDetail<GetLifeDetailV2Response>({
        id: contractId,
        contractType: ProductType.Clf,
        proposalNumber,
    });

    const title = useMemo(() => {
        if (!data?.productDescription) return "";
        return formatFufiTitle(data.productDescription, data.productName);
    }, [data?.productDescription, data?.productName, formatFufiTitle]);

    const subtitle = useMemo(() => {
        if (!isSuccess) return undefined;
        if (isDesktop) return `${cs.life.global.contractNumber} ${data.proposalNumber}`;
        return `${cs.life.global.contractNumber} ${data.proposalNumber}`;
    }, [isSuccess, isDesktop, data?.proposalNumber]);

    const personDescription = useMemo(() => {
        if (!isSuccess) return undefined;
        const firstPerson = findFufiFirstPerson(data.insuredPersons);
        if (!firstPerson) return null;

        const parts = [
            firstPerson.titlePrefix,
            firstPerson.firstName,
            firstPerson.lastName,
            firstPerson.titleSuffix,
        ].filter(Boolean);

        return `${cs.life.global.owner}: ${parts.join(" ")}`;
    }, [isSuccess, data?.insuredPersons]);

    const amount = useMemo(() => {
        if (!isSuccess) return;
        return `${cs.frequencies[data.paymentFrequency]} ${cs.global.dueInsurance}: ${NumberToCZK(data.premiumClient)}`;
    }, [isSuccess, data?.paymentFrequency, data?.premiumClient]);

    if (isError) {
        return (
            <ContractCardHeaderView
                title={cs.contractTypeToTitle.Clf}
                subtitle={`${cs.life.global.contractNumber} ${proposalNumber ?? contractId}`}
                personDescription={null}
            />
        );
    }

    return (
        <ContractCardHeaderView
            title={title}
            subtitle={subtitle}
            personDescription={personDescription}
            amount={amount}
            link={contractLink(contractId, ProductType.Clf)}
        />
    );
};

type IContractCardCarrets = {
    isFirst: boolean;
    isLast: boolean;
    onShift: (modifier: number) => void;
};

export const ContractHeaderMobileCarrets: FC<PropsWithChildren<IContractCardCarrets>> = ({
    isLast,
    isFirst,
    onShift,
    children,
}) => {
    return (
        <MobileCarretsRow direction="row" gap={1}>
            <CarretWrapper
                onClick={() => {
                    onShift(-1);
                }}
            >
                <CaretLeftIcon hidden={isFirst} />
            </CarretWrapper>
            {children}
            <CarretWrapper
                onClick={() => {
                    onShift(1);
                }}
            >
                <CaretRightIcon hidden={isLast} />
            </CarretWrapper>
        </MobileCarretsRow>
    );
};
